import { Injectable } from '@angular/core';

import { environment } from '../../environments/environment';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CommonFunction } from '../helpers/commonFunction';
import { ChatbotExtractRequest } from '../structure/chatbot/chatbot.models';
import { SKIP_LOADER } from '../interceptor/loader.interceptor';

@Injectable({
  providedIn: 'root'
})
export class ChatbotService {

  private readonly skipLoaderContext = new HttpContext().set(SKIP_LOADER, true);

  constructor(private http: HttpClient, private commonFunction: CommonFunction) { }

  /**
   * POST /robocs/extract
   * - Free text: { text: "..." }
   * - Meeting fields: { start_date, start_time, meeting_type } (any subset)
   */
  sendMessage(payload: string | ChatbotExtractRequest): Promise<any> {
    var user = JSON.parse(localStorage.getItem("user"));
    var resources = JSON.parse(localStorage.getItem("resources"));
    var companyDetails = JSON.parse(localStorage.getItem("companyDetailsGoveva"));

    var decrEmailId = this.commonFunction.decrypt(user.EmailId);
    var resourcesData = resources.filter(function (item) {
      return item.Menu === 'UPSI Detail All';

    })[0]

    var upsiAdmin;
    if (resourcesData) {
      upsiAdmin = (resourcesData['Menu']) ? true : false
    } else {
      upsiAdmin = false
    }
    var loginUser = {
      id: user._id,
      firstName: user.FirstName,
      lastName: user.LastName,
      upsiAdmin: upsiAdmin,

      EmailId: decrEmailId,
      companyDetails: companyDetails,
      title: user.Title
    }

    var PSSDetail = localStorage.getItem('companyDetails');

    var UserDetail = JSON.stringify(loginUser);
    let headers = {};
    let head = (key, header) => { headers[key] = header };
    head('Content-Type', 'application/json');
    head('Access-Control-Allow-Origin', '*');
    head('PSSDetail', PSSDetail);
    head('UserDetail', UserDetail);
    head('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);

    var token = localStorage.getItem('token')
    head("x-access-token", token)

    const body = this.buildExtractBody(payload);

    return this.http.post(environment.chatbotURL + '/robocs/extract', body, { headers })
      .toPromise()
      .then(response => {
        return Promise.resolve(response);
      })
  }

  private buildExtractBody(payload: string | ChatbotExtractRequest): ChatbotExtractRequest {
    if (typeof payload === 'string') {
      return { text: payload.trim() };
    }
    const directText = payload.text?.trim();
    if (directText) {
      return { text: directText };
    }

    const meetingType = payload.meeting_type?.trim();
    const startDate = payload.start_date?.trim();
    const startTime = payload.start_time?.trim();

    const parts: string[] = [];
    if (meetingType) {
      parts.push(`Meeting type: ${meetingType}`);
    }
    if (startDate) {
      parts.push(`Date: ${startDate}`);
    }
    if (startTime) {
      parts.push(`Time: ${startTime}`);
    }

    return { text: parts.join(', ') };
  }

  getBalanceSheet(symbol: string): Observable<any> {
    return this.http.get<any>(environment.chatbotURL + '/balance-sheet', {
      params: { symbol }
    });
  }

  getKeyRatios(symbol: string): Observable<any> {
    return this.http.get<any>(environment.chatbotURL + '/financial-ratios', {
      params: { symbol },
      context: this.skipLoaderContext,
    });
  }

  getIncomeStatement(symbol: string): Observable<any> {
    return this.http.get<any>(environment.chatbotURL + '/income-statement', {
      params: { symbol }
    });
  }

  getBalanceSheetStandalone(symbol: string): Observable<any> {
    const headers = {
      symbol: symbol
    };
    return this.http.get<any>(environment.clientUrl + 'financial/balance-sheet/standalone', {
      headers
    });
  }

  getBalanceSheetConsolidated(symbol: string): Observable<any> {
    const headers = {
      symbol: symbol
    };
    return this.http.get<any>(environment.clientUrl + 'financial/balance-sheet/consolidated', {    headers
    });
  }
  getPLQuarterlyStandalone(symbol: string): Observable<any> {
    const headers = {
      symbol: symbol
    };
    return this.http.get<any>(environment.clientUrl + 'financial/pl/standalone/quarterly', {
      headers
    });
  }
  getPLQuarterlyConsolidated(symbol: string): Observable<any> {
    const headers = {
      symbol: symbol
    };
    return this.http.get<any>(environment.clientUrl + 'financial/pl/consolidated/quarterly', {
      headers
    });
  }

  getPLAnnualStandalone(symbol: string): Observable<any> {
    const headers = {
      symbol: symbol
    };
    return this.http.get<any>(environment.clientUrl + 'financial/pl/standalone/annual', {
      headers
    });
  }
  getPLAnnualConsolidated(symbol: string): Observable<any> {
    const headers = {
      symbol: symbol
    };
    return this.http.get<any>(environment.clientUrl + 'financial/pl/consolidated/annual', {
      headers
    });
  }

  // ─── Financial Data Extractor API (financialExtractURL) ───────────────────
  // Used when environment.useFinancialExtractAPI = true

  /** GET /fmp/share-info/{symbol} — current price, 52w high/low, market cap, PE, book value */
  getFmpShareInfo(symbol: string): Observable<any> {
    return this.http.get<any>(
      `${environment.financialExtractURL}/fmp/share-info/${encodeURIComponent(symbol)}`,
      { context: this.skipLoaderContext }
    );
  }

  /** GET /fmp/key-ratios/{symbol} — earnings, margin, growth & return ratios (quarterly + annual) */
  getFmpKeyRatios(symbol: string): Observable<any> {
    return this.http.get<any>(
      `${environment.financialExtractURL}/fmp/key-ratios/${encodeURIComponent(symbol)}`,
      { context: this.skipLoaderContext }
    );
  }

  /** GET /fmp/income-statement/{symbol} — annual and quarterly income statement */
  getFmpIncomeStatement(symbol: string): Observable<any> {
    return this.http.get<any>(
      `${environment.financialExtractURL}/fmp/income-statement/${encodeURIComponent(symbol)}`,
      { context: this.skipLoaderContext }
    );
  }

  /** GET /fmp/balance-sheet/{symbol} — annual and quarterly balance sheet by symbol */
  getFmpBalanceSheetBySymbol(symbol: string): Observable<any> {
    return this.http.get<any>(
      `${environment.financialExtractURL}/fmp/balance-sheet/${encodeURIComponent(symbol)}`,
      { context: this.skipLoaderContext }
    );
  }

  /** POST /balance-sheet/extract-pdf — upload a balance-sheet PDF; returns { pdf_id } */
  extractBalanceSheetPdf(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(`${environment.financialExtractURL}/balance-sheet/extract-pdf`, formData);
  }

  /** GET /balance-sheet/data?pdf_id=&year= — extract balance sheet rows from a previously uploaded PDF */
  getBalanceSheetData(pdfId: string, year?: string): Observable<any> {
    const params: any = { pdf_id: pdfId };
    if (year) { params['year'] = year; }
    return this.http.get<any>(`${environment.financialExtractURL}/balance-sheet/data`, { params });
  }

  /** POST /pnl/extract-pdf — upload a P&L PDF; returns { pdf_id } */
  extractPnlPdf(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(`${environment.financialExtractURL}/pnl/extract-pdf`, formData);
  }

  /** GET /pnl/quarterly?pdf_id= — extract quarterly P&L from a previously uploaded PDF */
  getPnlQuarterly(pdfId: string): Observable<any> {
    return this.http.get<any>(`${environment.financialExtractURL}/pnl/quarterly`, { params: { pdf_id: pdfId } });
  }

  /** GET /pnl/yearly?pdf_id= — extract annual P&L from a previously uploaded PDF */
  getPnlYearly(pdfId: string): Observable<any> {
    return this.http.get<any>(`${environment.financialExtractURL}/pnl/yearly`, { params: { pdf_id: pdfId } });
  }
}
